import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Search, Copy, Check, X, Mail, Share2 } from "lucide-react";
import FindPeople, { Modal } from "@/components/FindPeople";
import { useDialog } from "@/components/Dialogs";

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

function Panel({ title, icon: Icon, children, testid }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
      <h2 className="flex items-center gap-2 font-serif text-xl font-semibold"><Icon className="h-5 w-5 text-[#C89B3C]" /> {title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

/** The "Invite friends" pop-up: the invite link and the invite-by-email form. */
function InviteDialog({ code, onReset, onClose }) {
  const [email, setEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const sendEmailInvite = async (e) => {
    e.preventDefault();
    if (!validEmail || emailBusy) return;
    setEmailBusy(true); setEmailMsg("");
    try { const r = await api.post("/invites/email", { email: email.trim() }); setEmailMsg(r.data.message || "Invitation on its way."); setEmail(""); }
    catch (err) { toast.error(errText(err, "Could not send that. Please try again.")); }
    finally { setEmailBusy(false); }
  };
  const link = code ? `${window.location.origin}/invite/${code}` : "";
  const copy = async () => { try { await navigator.clipboard.writeText(link); toast.success("Invite link copied"); } catch { toast.error("Could not copy. Select the link and copy it."); } };
  // The WhatsApp and Share buttons, and the "ready-written message" link, open the person's own app; nothing is sent by us.
  const inviteText = `Hi,\n\nI've been keeping a theatre diary on My Theatre (mytheatre.uk): the shows I've seen, ratings and notes. If you join, we can share our diaries and see what each other thought.\n\nHere's my invite link:\n${link}\n\nSee you at the theatre! 🎭`;
  const mailto = validEmail
    ? `mailto:${encodeURIComponent(email.trim())}?subject=${encodeURIComponent("Join me on My Theatre")}&body=${encodeURIComponent(inviteText)}`
    : undefined;
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(inviteText)}`;
  const share = async () => { try { await navigator.share({ title: "Join me on My Theatre", text: inviteText, url: link }); } catch { /* cancelled */ } };
  return (
    <Modal title="Invite friends" icon={Mail} onClose={onClose} testid="invite-friends">
          <p className="text-sm text-muted-foreground">For friends who aren't on My Theatre yet. They sign up through your link and can ask to be your friend straight away.</p>
          <div className="mt-4 border-t border-border pt-4">
            <p className="text-sm font-medium">Share your invite link</p>
            <p className="mt-1 break-all text-xs text-muted-foreground" data-testid="invite-link">{link}</p>
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={copy} data-testid="copy-invite" className="inline-flex items-center gap-1.5 rounded-full border border-[#C89B3C]/60 px-4 py-1.5 text-sm hover:bg-[#C89B3C]/15"><Copy className="h-4 w-4" /> Copy link</button>
              <button type="button" onClick={onReset} className="text-xs text-muted-foreground underline">Reset</button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Anyone who opens it can ask to be your friend. You still choose whether to accept.</p>
            <div className="mt-4">
              <p className="text-sm font-medium">Invite someone by email</p>
              <form onSubmit={sendEmailInvite} className="mt-2 flex gap-2" data-testid="invite-email-form">
                <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setEmailMsg(""); }} placeholder="friend@example.com" aria-label="Friend's email address"
                  data-testid="invite-email" className="min-w-0 flex-1 rounded-lg border border-border bg-background px-3 py-2 text-base" />
                <button type="submit" disabled={!validEmail || emailBusy} data-testid="send-email-invite"
                  className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold ${validEmail && !emailBusy ? "bg-[#C89B3C] text-black" : "bg-secondary text-muted-foreground opacity-60"}`}><Mail className="h-4 w-4" /> {emailBusy ? "Sending…" : "Send invitation"}</button>
              </form>
              {emailMsg && <p className="mt-2 text-sm text-[hsl(var(--gold))]" data-testid="invite-email-result">{emailMsg}</p>}
              <p className="mt-1 text-xs text-muted-foreground">We email them once, from My Theatre, with your username and your invite link. We don't keep their address. You can send 3 a day.
                {validEmail && <> Prefer your own email app? <a href={mailto} data-testid="email-invite" className="underline">Open a ready-written message</a>.</>}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a href={whatsapp} target="_blank" rel="noreferrer" data-testid="whatsapp-invite" className="inline-flex items-center gap-1.5 rounded-full border border-[#C89B3C]/60 px-4 py-1.5 text-sm hover:bg-[#C89B3C]/15">Send on WhatsApp</a>
                {typeof navigator !== "undefined" && navigator.share && (
                  <button type="button" onClick={share} data-testid="share-invite" className="inline-flex items-center gap-1.5 rounded-full border border-[#C89B3C]/60 px-4 py-1.5 text-sm hover:bg-[#C89B3C]/15"><Share2 className="h-4 w-4" /> Share…</button>
                )}
              </div>
            </div>
          </div>
    </Modal>
  );
}

export default function FriendsTab({ onGoDiary, onMessage }) {
  const dialog = useDialog();
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [find, setFind] = useState(null);       // null = closed, otherwise what was typed in the search bar
  const [invite, setInvite] = useState(false);
  const [top, setTop] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [f, c] = await Promise.all([
        api.get("/friends"), api.get("/friends/invite-code"),
      ]);
      setData(f.data); try { window.dispatchEvent(new CustomEvent("mytheatre:friends", { detail: { friends: f.data.friends.length, incoming: f.data.incoming } })); } catch { /* ignore */ } setCode(c.data.code);
    } catch {
      // A failed refresh keeps what is already on screen; only a first load that fails starts empty.
      setData((d) => d || { friends: [], incoming: [], outgoing: [], blocked: [], has_username: true, limit: 200 });
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Pick up new requests without a manual refresh: when the person comes back to the tab, and every 45 seconds while it is open.
  useEffect(() => {
    const again = () => { if (document.visibilityState !== "hidden") load(); };
    const id = setInterval(again, 45000);
    window.addEventListener("focus", again);
    document.addEventListener("visibilitychange", again);
    window.addEventListener("mytheatre:friends-changed", load);      // the banner answered a request
    return () => { clearInterval(id); window.removeEventListener("focus", again); document.removeEventListener("visibilitychange", again); window.removeEventListener("mytheatre:friends-changed", load); };
  }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(errText(e, "Something went wrong. Please try again.")); }
    finally { setBusy(false); }
  };


  const resetCode = async () => {
    if (!(await dialog.confirm({ title: "Reset your invite link?", body: "The old link will stop working.", confirmLabel: "Reset" }))) return;
    act(async () => { const r = await api.post("/friends/invite-code/reset"); setCode(r.data.code); }, "New invite link created");
  };
  const openSearch = (e) => { e.preventDefault(); if (top.trim().length >= 3) setFind(top.trim()); };

  if (!data) return <p className="text-muted-foreground">Loading…</p>;

  if (!data.has_username) {
    return (
      <div className="rounded-2xl border border-[#C89B3C]/30 bg-card p-8 text-center" data-testid="friends-need-username">
        <Users className="mx-auto h-9 w-9 text-[#C89B3C]" />
        <h2 className="mt-3 font-serif text-2xl font-semibold">Choose a username first</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">Friends find you by your username, so you need one before you can add anyone or be added. You can set it up in the Diary tab under "Share your diary".</p>
        <button type="button" onClick={onGoDiary} className="mt-5 rounded-full bg-[#C89B3C] px-6 py-2.5 text-sm font-semibold text-black">Go to the Diary tab</button>
      </div>
    );
  }

  const removeFriend = async (f) => {
    if (await dialog.confirm({ title: `Remove ${f.username} as a friend?`, body: "You'll stop seeing each other's friends-only entries, lists and notes.", confirmLabel: "Remove", danger: true }))
      act(() => api.delete(`/friends/user/${f.user_id}`), "Friend removed");
  };
  const blockFriend = async (f) => {
    if (await dialog.confirm({ title: `Block ${f.username}?`, body: "They won't be told, and neither of you will be able to see the other's diary.", confirmLabel: "Block", danger: true }))
      act(() => api.post("/friends/block", { username: f.username }), "Blocked");
  };

  return (
    <div>
      {dialog.node}
      <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="friends-topbar">
        <form onSubmit={openSearch} className="flex min-w-[15rem] flex-1 items-center gap-2" role="search">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <input value={top} onChange={(e) => setTop(e.target.value.toLowerCase())} maxLength={20} placeholder="Find people: start of a username"
              aria-label="Search for people by username" data-testid="people-q-top"
              className="w-full rounded-full border border-border bg-background py-2.5 pl-9 pr-4 text-base" />
          </div>
          <button type="submit" disabled={top.trim().length < 3} data-testid="people-search-top"
            className="rounded-full border border-[#C89B3C]/60 px-5 py-2.5 text-sm font-medium hover:bg-[#C89B3C]/15 disabled:opacity-50">Search</button>
        </form>
        <button type="button" onClick={() => setInvite(true)} data-testid="open-invite"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#C89B3C] px-5 py-2.5 text-sm font-semibold text-black hover:bg-[#d8ab4a]"><Mail className="h-4 w-4" /> Invite friends</button>
      </div>
      {find !== null && <FindPeople initialQuery={find} onChanged={load} onClose={() => setFind(null)} />}
      {invite && <InviteDialog code={code} onReset={resetCode} onClose={() => setInvite(false)} />}

      <div className="grid items-start gap-8 lg:grid-cols-2">
        <div className="space-y-8">
        <Panel title={`Friends (${data.friends.length})`} icon={Users} testid="friends-list">
          {data.friends.length === 0 ? (
            <p className="text-sm text-muted-foreground">No friends yet. Search for someone above, or use Invite friends. What your friends share appears in the Feed.</p>
          ) : (
            <ul className="space-y-2">
              {data.friends.map((f) => (
                <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <Link to={`/u/${f.username}`} className="font-medium hover:text-primary">{f.username}</Link>
                  <span className="flex gap-3 text-xs text-muted-foreground">
                    {onMessage && <button type="button" onClick={() => onMessage(f.username)} data-testid={`message-${f.username}`} className="font-medium text-[#C89B3C] hover:underline">Message</button>}
                    <button type="button" onClick={() => removeFriend(f)} className="hover:text-foreground">Remove</button>
                    <button type="button" onClick={() => blockFriend(f)} className="hover:text-red-400">Block</button>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {data.outgoing.length > 0 && (
            <div className="mt-5 border-t border-border pt-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Waiting for a reply</p>
              <ul className="mt-2 space-y-2">
                {data.outgoing.map((r) => (
                  <li key={r.id} className="flex items-center justify-between text-sm">
                    <span>{r.username}</span>
                    <button type="button" onClick={() => act(() => api.delete(`/friends/user/${r.user_id}`), "Request cancelled")} className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.blocked.length > 0 && (
            <div className="mt-5 border-t border-border pt-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Blocked</p>
              <ul className="mt-2 space-y-2">
                {data.blocked.map((r) => (
                  <li key={r.id} className="flex items-center justify-between text-sm">
                    <span>{r.username}</span>
                    <button type="button" onClick={() => act(() => api.delete(`/friends/block/${r.user_id}`), "Unblocked")} className="text-xs text-muted-foreground hover:text-foreground">Unblock</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Panel>
        {(data.declined || []).length > 0 && (
          <Panel title={`Declined requests (${data.declined.length})`} icon={X} testid="declined">
            <p className="mb-3 text-xs text-muted-foreground">Only you can see this. They weren't told you declined. If you've changed your mind, you can accept for up to 30 days.</p>
            <ul className="space-y-2">
              {data.declined.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border px-3 py-2.5">
                  <span className="min-w-0 break-words font-medium">{r.username}</span>
                  <button type="button" disabled={busy} data-testid={`reconsider-${r.username}`}
                    onClick={() => act(() => api.post(`/friends/${r.id}/reconsider`), `You and ${r.username} are now friends`)}
                    className="inline-flex items-center gap-1 rounded-full border border-[#C89B3C]/60 px-4 py-1.5 text-sm hover:bg-[#C89B3C]/15"><Check className="h-4 w-4" /> Accept after all</button>
                </li>
              ))}
            </ul>
          </Panel>
        )}

        </div>
      </div>
    </div>
  );
}